import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Hopper } from '../../app/models/Hopper';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import useHopperService from '../../app/services/hooks/useHopperService';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import { TruckFilters } from '../../app/models/Truck';
import HopperDataTable from './components/tables/HopperDataTable';
import HopperCreate from './components/HopperCreate';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Lista diaria de transportistas',
        url: '/hoppers',
        isActive: true
    }
];

const Hoppers = () => {
    const history = useHistory();
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { fetchingGetHoppers, getHoppers, deleteHopper, activeHopper } = useHopperService();
    const [hoppers, setHoppers] = useState<Hopper[]>([]);
    const { navigationPage } = useNavigationPage();

    const [hopperIdEdit, setHopperIdEdit] = useState<number>(-1);
    const [hopperIdShow, setHopperIdShow] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingShow, setShowingShow] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [filter, setFilter] = useState<TruckFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc',
        provider: '',
        society: '',
        patent: '',
        brand: '',
        vehicle_model: '',
        year: '',
        vin: ''
    });

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order]);

    const reloadTable = () => {
        getHoppers(filter, {
            onSuccess: (response: ServiceResponse) => {
                setHoppers(response.data.hoppers);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message, {
                    autoClose: 2000
                });

                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (hopperId: number) => {
        navigationPage('/hoppers/' + hopperId + '/edit');
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setHopperIdEdit(-1);
    };

    const showShow = (HopperId: number) => {
        setShowingShow(true);
        setHopperIdShow(HopperId);
    };

    const hideShow = () => {
        setShowingShow(false);
        setHopperIdShow(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const active = (hpperId: number, active: boolean) => {
        activeHopper(hpperId, active, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                reloadTable();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            }
        });
    };

    const destroy = (hopperId: number) => {
        const _text = 'Está a punto de eliminar el Semi Remolque #' + hopperId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando Semi Remolque...');
                        deleteHopper(hopperId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El Semi Remolque no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Semi remolque" breadcrumbs={breadcrumbs} />
            <DefaultCard>
                <div className="row">
                    <div className="col-12">
                        <div className="row">
                            <div className="col">
                                <ButtonCreate
                                    callbackCreate={showCreate}
                                    title="Nuevo Semi Remolque"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-12">
                        <HopperDataTable
                            hoppers={hoppers}
                            totalRows={totalRows}
                            loading={fetchingGetHoppers}
                            destroy={destroy}
                            edit={showEdit}
                            filter={filter}
                            setFilter={setFilter}
                            active={active}
                            callbackSuccess={reloadTable}
                            onSearch={reloadTable}
                        />
                    </div>
                </div>
            </DefaultCard>
            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Semi Remolque"
                    backdrop={true}
                    showFooter={false}
                >
                    <HopperCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default Hoppers;
